/**
 * Feature E04-F01 — Inbox typed api-client + Query hooks (T02 list / T03
 * dismissed / single-email fetch for T04 preview).
 *
 * Feature-owned (NOT in the shared queries.ts). Uses the shared `apiClient`
 * (auto-injects the session Bearer token, normalizes the response, never throws)
 * + `unwrap` (the single throw-at-the-Query-boundary helper). Contracts come from
 * `@eer/shared` so the SPA speaks the exact same shapes as the Fastify API.
 *
 * 1:1 port of `../EER app/public/assets/js/inbox.js`:
 *   GET /emails?classification=&limit=&offset=  ← loadEmails / loadMoreEmails (T02/T05)
 *   GET /emails?email_id=                        ← selectEmail full-body fetch (T04)
 *   GET /emails/dismissed                        ← get_dismissed (T03)
 *
 * The route returns `{ success:true, emails:[…] }`; the client's
 * `unwrapLegacySuccess` strips the `success` flag, so the unwrapped body is
 * `{ emails:[…] }` — validated here against the shared `emailSchema` so a drifted
 * shape fails loudly at the boundary rather than rendering garbage.
 */
import {
  useQuery,
  useInfiniteQuery,
  type UseQueryResult,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from '@tanstack/react-query';
import { z } from 'zod';
import {
  emailSchema,
  dismissedEmailSchema,
  EMAILS_PAGE_SIZE,
  type EmailDTO,
  type DismissedEmailDTO,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { qk } from '../api/queryKeys.js';

/** The unwrapped body shape (`success` already stripped by the client). */
const emailsBodySchema = z.object({ emails: z.array(emailSchema) });
const dismissedBodySchema = z.object({ emails: z.array(dismissedEmailSchema) });

/** One loaded page of the inbox feed (rows + the offset that produced them). */
export interface EmailsPage {
  readonly emails: EmailDTO[];
  /** The offset this page was fetched at (for `getNextPageParam`). */
  readonly offset: number;
}

/** Filters for an inbox list request. `classification:'all'` → omitted (legacy). */
export interface InboxListFilter {
  /** Classification tab value ('all' | 'existing_project' | …). */
  readonly classification?: string;
  /** Restrict to one project's mail (project conversation view reuse). */
  readonly projectId?: number;
}

/** Build the GET /emails query params (omitting `classification:'all'`). */
function listQueryParams(filter: InboxListFilter, offset: number): Record<string, string | number> {
  const params: Record<string, string | number> = {
    limit: EMAILS_PAGE_SIZE,
    offset,
  };
  if (filter.classification && filter.classification !== 'all') {
    params.classification = filter.classification;
  }
  if (filter.projectId != null) {
    params.project_id = filter.projectId;
  }
  return params;
}

/** Fetch one page of the inbox feed (GET /emails). */
export async function fetchEmailsPage(
  filter: InboxListFilter,
  offset: number,
): Promise<EmailsPage> {
  const result = await apiClient.get<unknown>('/emails', {
    command: 'emails',
    query: listQueryParams(filter, offset),
  });
  const body = emailsBodySchema.parse(unwrap(result));
  return { emails: body.emails, offset };
}

/** Fetch ONE email WITH the full body (GET /emails?email_id=) for the preview. */
export async function fetchEmailById(emailId: number): Promise<EmailDTO | null> {
  const result = await apiClient.get<unknown>('/emails', {
    command: 'emails',
    query: { email_id: emailId },
  });
  const body = emailsBodySchema.parse(unwrap(result));
  return body.emails[0] ?? null;
}

/** Fetch the dismissed (hidden) email list (GET /emails/dismissed). */
export async function fetchDismissedEmails(): Promise<DismissedEmailDTO[]> {
  const result = await apiClient.get<unknown>('/emails/dismissed', {
    command: 'emails',
  });
  const body = dismissedBodySchema.parse(unwrap(result));
  return body.emails;
}

/** Query key for an inbox feed keyed by its filter (under the `emails` domain). */
export function inboxListKey(filter: InboxListFilter): readonly unknown[] {
  return qk.emails.list({
    classification: filter.classification ?? 'all',
    project_id: filter.projectId,
  });
}

/** Query key for a single-email full-body read. */
export function inboxEmailKey(emailId: number): readonly unknown[] {
  return qk.emails.detail(emailId);
}

/** Query key for the dismissed list. */
export const inboxDismissedKey = [...qk.emails.all, 'dismissed'] as const;

/**
 * Paginated inbox feed (T02/T05). Infinite query: each page is one
 * `EMAILS_PAGE_SIZE` slice; `getNextPageParam` returns the next offset only
 * while the last page came back full (legacy `_emailsHasMore = received >= 50`).
 */
export function useInboxEmails(
  filter: InboxListFilter,
): UseInfiniteQueryResult<InfiniteData<EmailsPage>, ApiResultError> {
  return useInfiniteQuery<
    EmailsPage,
    ApiResultError,
    InfiniteData<EmailsPage>,
    readonly unknown[],
    number
  >({
    queryKey: inboxListKey(filter),
    queryFn: ({ pageParam }) => fetchEmailsPage(filter, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      // hasMore ⇔ the last page returned a full slice; next offset = sum of all
      // rows received so far (legacy `_emailsOffset += received`).
      if (lastPage.emails.length < EMAILS_PAGE_SIZE) return undefined;
      return lastPage.offset + lastPage.emails.length;
    },
  });
}

/**
 * Single-email full-body read (T04). Enabled only when an email is selected AND
 * its full body is not already present (the list rows carry only `body_preview`).
 */
export function useEmailFullBody(
  emailId: number | null,
  needsFetch: boolean,
): UseQueryResult<EmailDTO | null, ApiResultError> {
  return useQuery<EmailDTO | null, ApiResultError>({
    queryKey: emailId != null ? inboxEmailKey(emailId) : ['emails', 'detail', 'none'],
    queryFn: () => fetchEmailById(emailId as number),
    enabled: emailId != null && needsFetch,
    staleTime: 5 * 60 * 1000, // a fetched body never changes for the session
  });
}

/** Dismissed (hidden) email list (T03). */
export function useDismissedEmails(
  enabled: boolean = true,
): UseQueryResult<DismissedEmailDTO[], ApiResultError> {
  return useQuery<DismissedEmailDTO[], ApiResultError>({
    queryKey: inboxDismissedKey,
    queryFn: fetchDismissedEmails,
    enabled,
  });
}
